import React from 'react'
import { background } from 'assets/backgrounds'
import { useNavigate } from 'react-router-dom'
import {
  Background01Desktop,
  Background01Mobile,
  Background02Desktop,
  Background02Mobile,
  Background03Desktop,
  Background03Mobile,
  ConnectLine01,
  ConnectLine01Mobile,
  ConnectLine02,
  ConnectLine02Mobile,
  ConnectLine03,
  ConnectLine03Mobile,
} from './components/Background'
import headerImage from '../../assets/ComplaintAssistance/header.png'

const ComplaintCard = ({
  title,
  titleColor,
  className,
  children,
}: {
  title: string
  titleColor: string
  className: string
  children: React.ReactNode
}) => (
  <div
    className={`${className} p-5 md:p-10 rounded-xl md:rounded-3xl border md:border-[3px] backdrop-blur-xl flex-col justify-start items-start gap-6 inline-flex shadow-[0px_8px_40px_0px_rgba(0,0,0,0.12)]`}
  >
    <div
      style={{ color: titleColor }}
      className='self-stretch text-base md:text-2xl font-medium md:font-bold leading-loose tracking-wide'
    >
      {title}
    </div>
    <div className='self-stretch text-justify'>
      <span className='text-[#49505c] text-xs md:text-base flex flex-col gap-6 leading-tight tracking-wide'>
        {children}
      </span>
    </div>
  </div>
)

const ComplaintAssistance = () => {
  const navigate = useNavigate()

  return (
    <>
      <div
        className='flex flex-col pt-10 px-[20px] items-center xl:items-start gap-10
          lg:pt-[120px] xl:flex-row xl:gap-x-[143px] xl:pl-[172px]'
      >
        <div className='max-w-[946px] flex flex-col justify-start lg:items-start text-center lg:text-start gap-8 lg:gap-10'>
          <div className='flex flex-col justify-start items-center lg:items-start gap-3 md:gap-7'>
            <div className='text-[#040506] text-lg sm:text-2xl md:text-3xl lg:text-[42px] xl:text-[64px] font-bold leading-none lg:leading-tight'>
              Complaint Assistance
            </div>
            <div className='text-[#24385c] text-sm md:text-lg font-medium md:font-bold leading-normal md:tracking-wide'>
              Helping you access the benefits and protections of using a
              regulated UK lawyer or firm.
            </div>
          </div>
          <div className='text-[#6e7685] text-xs md:text-base font-normal leading-tight md:tracking-wide'>
            At Legal Utopia, we're committed to ensuring that your experience
            with a regulated legal services provider via our platform is smooth
            and positive, and you get the best possible outcome. That's why,
            when you book with a regulated lawyer through our platform, you
            receive our Complaint Assistance. This service is here to support
            you if you are not satisfied with the professional conduct, or the
            quality, of service you received from your lawyer or film and need
            help making a formal complaint.
          </div>
        </div>
        <div className='lg:mt-[69px] px-2'>
          <img
            src={headerImage}
            className='flex w-[318px] lg:w-[490px] h-[200px] lg:h-[339px] rounded-lg object-cover'
          />
        </div>
      </div>

      <div className='relative flex justify-start md:pl-[216px] px-6 pt-[78px] lg:pt-[132px]'>
        <div className='absolute left-0 top-[-245px] z-[-10] hidden md:block'>
          <Background01Desktop />
        </div>
        <div className='absolute top-[-80px] left-[-60px] z-[-10] md:hidden'>
          <Background01Mobile />
        </div>
        <ComplaintCard
          title='What is Complaint Assistance?'
          titleColor='#205497'
          className='w-[866px] border-[#6596D7]'
        >
          <p className='font-normal md:font-medium'>
            Complaint Assistance is a service we provide to customers who have:
          </p>
          <ol className='list-inside list-decimal text-left pl-6 md:pl-9'>
            <li>Created, and verified, an account with Legal Utopia;</li>
            <li>
              Made a booking with a regulated solicitor or barrister using Legal
              Utopia's Book a Lawyer service;
            </li>
            <li>
              Interacted and/or engaged with a regulated solicitor or barrister,
              or the regulated firm they are associated with, via Legal Utopia's
              services;
            </li>
            <li>
              Attempted to resolve the issue directly with the solicitor or
              barrister; or their regulated firm; and
            </li>
            <li>
              Exhausted their internal complaints process (or not received a
              response within its stated timeframe).
            </li>
          </ol>
          <p className='whitespace-pre-line'>
            If you encounter issues with the legal service provided, our team is
            ready to help you make a complaint if you believe this is the most
            appropriate course of action. Complaint Assistance provides support
            with complaints to the:
            <ul className='list-inside list-disc pt-6 pl-6 md:pl-9 font-normal md:font-medium text-left'>
              <li>Solicitors Regulation Authority Limited</li>
              <li>Bar Standards Board</li>
              <li>The Legal Ombudsman</li>
            </ul>
          </p>
          <p>
            If you do need to make an official complaint, we will help to make
            sure that your concerns are heard and addressed by the service
            provider engaged on our platform, and, if necessary, their regulator
            or the Legal Ombudsman.
          </p>
        </ComplaintCard>
        <div className='absolute bottom-[-90px] right-0 z-[-10] md:hidden'>
          <Background02Mobile />
        </div>
      </div>

      <div className='relative flex justify-end pl-[50px] pr-[26px] md:pr-[252px] pt-[152px] md:pt-[52px]'>
        <div className='relative top-[-310px] left-[190px] z-[-1] hidden xl:block'>
          <ConnectLine01 />
        </div>
        <div className='absolute top-0 left-[70px] md:hidden'>
          <ConnectLine01Mobile />
        </div>
        <div className='absolute md:w-[420px] right-[100px] top-[-210px] z-[-10] hidden md:block'>
          <Background02Desktop />
        </div>
        <ComplaintCard
          title='How does it work?'
          titleColor='#239687'
          className='w-[762px] border-[#58c0b2]'
        >
          <p>
            <p className='font-medium'>
              First use the Lawyer's own Complaints Process
            </p>
            <p>
              Before accessing Complaint Assistance, start by raising your
              concern(s) directly with the lawyer or law firm. This is usually
              the quickest way to address a problem. All regulated lawyers have
              to have an internal complaints process to handle any issues their
              clients may raise.
            </p>
          </p>
          <p>
            <p className='font-medium'>
              If this doesn't work, contact us for support
            </p>
            <p>
              If you've completed the lawyer's internal process and your issue
              hasn't been satisfactorily addressed, Complaint Assistance can
              step in. We will help take your complaint further, either to the
              relevant regulatory body or to the Legal Ombudsman.
            </p>
          </p>
          <p>
            <p className='font-medium'>
              Guiding you through the Complaints Process
            </p>
            <p>
              Our team will provide you with guidance on what to expect and how
              to present your concerns clearly and effectively. While we don't
              handle the complaint directly, we'll make sure you have the tools
              and knowledge you need to proceed.
            </p>
          </p>
        </ComplaintCard>
      </div>

      <div className='relative flex pl-[30px] md:pl-[252px] pt-[111px] md:pt-[57px] pr-[55px]'>
        <div className='relative z-[-1] top-[-615px] left-[656px] hidden xl:block'>
          <ConnectLine02 />
        </div>
        <div className='absolute md:hidden top-0 left-[143px]'>
          <ConnectLine02Mobile />
        </div>
        <ComplaintCard
          title='Who Handles Your Complaint?'
          titleColor='#3131b0'
          className='w-[762px] border-[#8484f5]'
        >
          <p>Your complaint may be reviewed by one of the following:</p>
          <div>
            <p className='font-medium'>The Lawyer's Regulatory Body</p>
            <p className='font-normal'>
              All regulated lawyers in the UK, whether a solicitor or barrister,
              are overseen by a professional regulatory body. These
              organisations, such as the Solicitors Regulation Authority (SRA)
              or the Bar Standards Board (BSB), ensure that lawyers meet
              professional standards. If your issue concerns a lawyer’s conduct
              or quality of service, the regulatory body can investigate and
              take action if needed.
            </p>
          </div>
          <div>
            <p className='font-medium'>The Legal Ombudsman</p>
            <p className='font-normal'>
              The Legal Ombudsman is an independent body that reviews complaints
              about legal services. If you have tried to resolve your issue
              directly with the lawyer and the regulatory body, the Legal
              Ombudsman can step in to review the case impartially, working to
              find a fair resolution for both parties.
            </p>
          </div>
        </ComplaintCard>
        <div className='absolute bottom-[-120px] left-[-25px] z-[-10] md:hidden'>
          <Background03Mobile />
        </div>
      </div>

      <div className='relative flex justify-end items-end pr-[26px] md:pr-[252px] pb-[60px] lg:pb-[230px] pt-[204px] md:pt-[49px] pl-[60px]'>
        <div className='relative z-[-1] top-[-830px] right-[-7%] hidden xl:block'>
          <ConnectLine03 />
        </div>
        <div className='absolute md:hidden top-0 right-[45px]'>
          <ConnectLine03Mobile />
        </div>
        <div className='absolute md:left-[23%] top-[-220px] z-[-10] hidden md:block'>
          <Background03Desktop />
        </div>
        <ComplaintCard
          title='Why We Offer Complaint Assistance'
          titleColor='#1c7d92'
          className='w-[762px] border-[#65c2d7]'
        >
          <p>
            Booking a lawyer is a big decision, and we believe you deserve peace
            of mind throughout the process. With Complaint Assistance, you have
            added reassurance that if something goes wrong, we're here to
            support you.
          </p>
          <p>
            Important to Note: Complaint Assistance only applies after you've
            gone through the lawyer's own complaints process. It's also designed
            as a guidance service, helping you navigate the formal complaint
            channels.
          </p>
        </ComplaintCard>
      </div>

      <div className='flex flex-col items-center gap-6 pb-[54px]'>
        <h1 className='text-base md:text-2xl text-[#001A3D] font-medium md:font-bold '>
          Still Have Questions?
        </h1>
        <div className='flex flex-col items-center gap-8 text-center'>
          <p className='px-[20px] text-xs md:text-base text-[#24385c] tracking-wide'>
            If you'd like more information about Complaint Assistance or need
            assistance with a booking, please reach out to our customer support
            team.
            <p>
              We're here to help you feel confident and secure in your
              experience with Legal Utopia.
            </p>
          </p>
          <div>
            <button
              onClick={() => navigate('/contact-us')}
              className='px-[8px] md:px-[16px] py-[4px] md:py-[10px] text-xs md:text-base rounded md:rounded-lg text-[#1c7d92] font-medium shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] tracking-wide'
            >
              Get Support
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
export default ComplaintAssistance
